/**
 * Hook to assign moderation cases for a topic
 */

import { useLazyQuery } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { useMemo } from 'react';

import { ASSIGN_MODERATION_CASE } from '../graphql';

interface ModerationCase {
  id: string;
  communityId: string;
  topicName: string;
  subjectId: string;
  caseId: string;
  pk: string;
  report: {
    communityId: string;
    topicName: string;
    subjectId: string;
    timestamp: string;
    contents: Array<{
      contentId: string;
      contentType: string;
      data: string;
      labels: Array<{
        labels: Array<{
          label: string;
          confidence: number;
        }>;
        source: string | null;
      }> | null;
    }> | null;
  };
  status: string;
  createdBy: string;
  createdAt: string;
  lastModifiedBy: string;
  lastModifiedAt: string;
}

interface AssignModerationCaseResponse {
  assignModerationCase: ModerationCase[] | null;
}

/**
 * Hook to assign moderation cases to the current moderator
 * @returns Lazy query function to assign cases
 */
export function useAssignModerationCase() {
  const [assignCases, { data, loading, error }] = useLazyQuery<AssignModerationCaseResponse>(ASSIGN_MODERATION_CASE, {
    fetchPolicy: 'network-only', // Always fetch fresh cases from server
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  const cases: ModerationCase[] = data?.assignModerationCase || [];

  return {
    assignCases,
    cases,
    loading,
    errors,
  };
}
